/**
 * Project registry: list of projects (name + creation date + wrapped key).
 * Project *names* are stored in plain text here so the home screen can list
 * them without a password; only entering a project (deriving its key) needs
 * one. Actual document content is encrypted separately — see fs.ts.
 */
import { Cipher } from "./crypto/cipher";
import { fs, PROJECTS_REGISTRY_PATH, verifyProjectKey } from "./fs";
import { slugify, isSlug } from "../utils/slug";

const RESERVED_PROJECT_SEGMENT = "__projects__";

export interface ProjectMeta {
  /**
   * Stable identifier / storage key: lowercase letters, digits, hyphens only
   * (see isValidProjectName). This is what every fs path, cloud key, and
   * registry lookup keys on — it must be URL/path-safe, which is exactly why
   * it's kept separate from the free-text `label`.
   */
  name: string;
  /** Human-readable display name — any characters. Auto-derived from, but editable independently of, `name`. */
  label: string;
  createdAt: string;
  /** base64 of Cipher.encrypt(base64(rawProjectKey), password) */
  wrappedKey: string;
}

function toBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

function fromBase64(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function importProjectKey(rawKey: Uint8Array): Promise<CryptoKey> {
  // Extractable so it can be cached across reloads (see cacheProjectKeySession
  // below) — sessionStorage already holds the exported form, so marking the
  // live CryptoKey non-extractable would add no real protection.
  return crypto.subtle.importKey("raw", rawKey, { name: "AES-GCM", length: 256 }, true, [
    "encrypt",
    "decrypt",
  ]);
}

/**
 * Session-only cache of the active project's derived key, so a page reload
 * doesn't force re-entering the password — but closing the tab/browser does
 * (sessionStorage, not localStorage). "Logout"/"Switch Project" clear this
 * explicitly to lock the project again without waiting for the tab to close.
 */
const SESSION_CACHE_KEY = "qda-project-key-cache";
const ROOM_SECRET_CACHE_KEY = "qda-room-secret-cache";

export async function cacheProjectKeySession(projectName: string, key: CryptoKey): Promise<void> {
  try {
    const jwk = await Cipher.exportKeyToJWK(key);
    sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify({ projectName, jwk }));
  } catch {
    // Non-fatal — worst case the user just has to re-enter the password after a reload.
  }
}

export async function loadCachedProjectKey(projectName: string): Promise<CryptoKey | null> {
  try {
    const raw = sessionStorage.getItem(SESSION_CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (cached?.projectName !== projectName || typeof cached.jwk !== "string") return null;
    return await Cipher.importKeyFromJWK(cached.jwk);
  } catch {
    return null;
  }
}

export function clearCachedProjectKey(): void {
  try {
    sessionStorage.removeItem(SESSION_CACHE_KEY);
    sessionStorage.removeItem(ROOM_SECRET_CACHE_KEY);
  } catch {
    // Silently fail if sessionStorage is unavailable
  }
}

/**
 * The collaboration room secret is derived from the project name + password —
 * NOT the encryption key — so that anyone who opens the same project name with
 * the same password joins the same y-webrtc room, regardless of whether their
 * local copies happen to share the (random, per-creation) data key. It's cached
 * in sessionStorage the same way the key is: survives a reload, cleared on tab
 * close / logout, so the room can be re-derived after a reload without the
 * plaintext password being retained anywhere.
 */
export async function cacheRoomSecret(projectName: string, password: string): Promise<string> {
  const secret = await Cipher.hash(`${projectName}\n${password}`);
  try {
    sessionStorage.setItem(ROOM_SECRET_CACHE_KEY, JSON.stringify({ projectName, secret }));
  } catch {
    // Non-fatal — collaboration just won't auto-resume after a reload.
  }
  return secret;
}

export function loadRoomSecret(projectName: string): string | null {
  try {
    const raw = sessionStorage.getItem(ROOM_SECRET_CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    return cached?.projectName === projectName && typeof cached.secret === "string"
      ? cached.secret
      : null;
  } catch {
    return null;
  }
}

/** Derive a project name slug from a free-text label (see utils/slug's slugify). */
export const slugifyProjectName = slugify;

/**
 * A valid project name is a non-empty slug (see isSlug), with the reserved
 * registry segment excluded (its underscores already fail the slug pattern,
 * but the guard documents the intent).
 */
export function isValidProjectName(name: string): boolean {
  return isSlug(name) && name !== RESERVED_PROJECT_SEGMENT;
}

/**
 * Refuse to enter a project with a key that can't decrypt its stored data (see
 * fs.verifyProjectKey). Without this, writing under a mismatched key silently
 * corrupts the project — the next unlock derives the registry's key, which no
 * longer matches — so we throw a clear, distinct error (not "Incorrect
 * password": the password *did* unwrap a key; it just isn't the right one for
 * the data on this device) before any write can happen.
 */
async function assertKeyMatchesProject(name: string, key: CryptoKey): Promise<void> {
  if (!(await verifyProjectKey(name, key))) {
    throw new Error(
      "This project's stored documents can't be decrypted with this key. The " +
        "password may belong to a different copy of the project (e.g. one re-created " +
        "or restored with a different key). Opening is blocked to avoid corrupting them."
    );
  }
}

export async function listProjects(): Promise<ProjectMeta[]> {
  const list = await fs.readFile(PROJECTS_REGISTRY_PATH);
  if (!Array.isArray(list)) return [];
  // Backfill `label` for pre-label registry entries so the UI always has a
  // display string — legacy projects just show their slug name as the label.
  return list.map((p) => ({
    ...p,
    label: typeof p.label === "string" && p.label ? p.label : p.name,
  }));
}

async function saveRegistry(projects: ProjectMeta[]): Promise<void> {
  await fs.writeFile(PROJECTS_REGISTRY_PATH, projects);
}

/**
 * Adopt a project whose record came from the cloud (see
 * qdaSync.fetchCloudProjects) onto this device. Verifies the password by
 * unwrapping the cloud-provided wrapped key — which recovers the exact random
 * data key that encrypted the project's documents, so they'll decrypt here —
 * then persists the project into the local registry and returns the live key.
 * A wrong password fails the unwrap (AES-GCM auth-tag mismatch), exactly like
 * unlockProject.
 */
export async function importProject(meta: ProjectMeta, password: string): Promise<CryptoKey> {
  let rawKeyB64: string;
  try {
    rawKeyB64 = await Cipher.decrypt(fromBase64(meta.wrappedKey), password);
  } catch {
    throw new Error("Incorrect password for this project.");
  }
  const key = await importProjectKey(fromBase64(rawKeyB64));
  // Verify before persisting: if this project already has data on the device
  // (e.g. a same-named local copy created with a different key), a mismatched
  // cloud key must not become the active key or the next write corrupts it.
  await assertKeyMatchesProject(meta.name, key);
  const projects = await listProjects();
  if (!projects.some((p) => p.name === meta.name)) {
    await saveRegistry([
      ...projects,
      {
        name: meta.name,
        label: meta.label || meta.name,
        createdAt: meta.createdAt || new Date().toISOString(),
        wrappedKey: meta.wrappedKey,
      },
    ]);
  }
  return key;
}

/**
 * Create a new project: generates a random project data key, wraps it with
 * the password (Cipher.encrypt derives its own random salt+iv per call, so
 * every project gets an independently-salted wrap with no extra bookkeeping),
 * and returns the live key so the caller can enter the project immediately
 * without a second password prompt.
 */
export async function createProject(
  name: string,
  label: string,
  password: string
): Promise<{ meta: ProjectMeta; key: CryptoKey }> {
  const trimmedName = name.trim();
  const trimmedLabel = label.trim();
  if (!isValidProjectName(trimmedName)) {
    throw new Error("Project name must be lowercase letters, numbers, and hyphens.");
  }
  if (!trimmedLabel) {
    throw new Error("Enter a project label.");
  }
  if (!password) {
    throw new Error("A password is required.");
  }

  const projects = await listProjects();
  if (projects.some((p) => p.name === trimmedName)) {
    throw new Error(`A project named "${trimmedName}" already exists.`);
  }

  const rawKey = crypto.getRandomValues(new Uint8Array(32));
  const wrapped = await Cipher.encrypt(toBase64(rawKey), password);
  const key = await importProjectKey(rawKey);

  // Refuse to mint a fresh key over documents that already exist under this
  // name — orphaned from a previous project of the same name (registry entry
  // gone, encrypted files left behind). A new random key can't decrypt them,
  // which is exactly the mismatch verifyProjectKey guards against; creating
  // here would strand that data for good.
  if (!(await verifyProjectKey(trimmedName, key))) {
    throw new Error(
      `Storage for "${trimmedName}" already exists on this device but can't be read with a ` +
        `new key. Open it with its original project password instead of creating it again.`
    );
  }

  const meta: ProjectMeta = {
    name: trimmedName,
    label: trimmedLabel,
    createdAt: new Date().toISOString(),
    wrappedKey: toBase64(wrapped),
  };
  await saveRegistry([...projects, meta]);
  return { meta, key };
}

/**
 * Delete a project from this device: drop its registry entry and wipe all of
 * its stored files (documents, working directory, xml). No password needed —
 * the data is being destroyed, not read. Local only: it does not delete the
 * project from any cloud backend, though a subsequent sync will push this
 * device's now-shorter registry (see qdaSync.pushRegistry). If the deleted
 * project's key is cached for the session, that cache is dropped too.
 */
export async function deleteProject(name: string): Promise<void> {
  const projects = await listProjects();
  const remaining = projects.filter((p) => p.name !== name);
  if (remaining.length !== projects.length) {
    await saveRegistry(remaining);
  }
  await fs.deleteProjectData(name);
  // loadCachedProjectKey only returns a key when the cache is for this exact
  // project, so a hit means the session cache must be cleared to avoid leaving
  // a key for a project that no longer exists.
  if (await loadCachedProjectKey(name)) clearCachedProjectKey();
}

/**
 * Unlock an existing project. A wrong password fails to decrypt (AES-GCM
 * auth tag mismatch) — that failure IS the password check, no separate
 * verifier is stored.
 */
export async function unlockProject(name: string, password: string): Promise<CryptoKey> {
  const projects = await listProjects();
  const meta = projects.find((p) => p.name === name);
  if (!meta) {
    throw new Error(`Project "${name}" not found.`);
  }

  let rawKeyB64: string;
  try {
    rawKeyB64 = await Cipher.decrypt(fromBase64(meta.wrappedKey), password);
  } catch {
    throw new Error("Incorrect password.");
  }
  const key = await importProjectKey(fromBase64(rawKeyB64));
  await assertKeyMatchesProject(name, key);
  return key;
}
